import TextField from "@/components/TextField";
import Button from "@/components/Button";
import Select from "@/components/Select";
import Banner from "@/components/Banner";
import { EventRegistrationAdditionalStatus, RegistrantRow } from "../types";
import { useForm, Controller } from "react-hook-form";
import { patSubmissionStatusOptions } from "../_utils";
import useModal from "@/hooks/useModal";

interface UpdateStatusModalProps {
  onClose: () => void;
  onSave: (status: string, note: string) => void;
  eventRegistration: RegistrantRow;
}

export default function UpdateStatusModal({ onClose, onSave, eventRegistration }: UpdateStatusModalProps) {
  const { Modal } = useModal();
  const {
    control,
    formState: { errors },
    trigger,
    getValues,
    watch
  } = useForm({
    defaultValues: {
      status: eventRegistration.additionalStatus,
      note: eventRegistration.note || ""
    },
    mode: "onSubmit"
  });

  const watchedStatus = watch("status");

  const handleSave = async () => {
    const result = await trigger();
    const formValues = getValues();

    if (result) {
      onSave(formValues.status, formValues.note);
      onClose();
    }
  };

  const action = <Button onClick={() => handleSave()}>Save</Button>;

  return (
    <Modal
      displayOverflow
      onClosed={onClose}
      headline="Update Status"
      data-testid="updatestatus-modal"
      action={action}
      className="max-w-md min-w-[600px]"
    >
      <div className="flex flex-col gap-6">
        <Controller
          control={control}
          name="status"
          rules={{ required: "Status is required" }}
          render={({ field: { onChange, value } }) => {
            return (
              <Select
                label="Status"
                required
                value={value}
                options={patSubmissionStatusOptions([EventRegistrationAdditionalStatus.EXCUSED])}
                onSelect={onChange}
                className="w-full"
                error={!!errors.status}
                errorText={errors.status?.message as string}
              />
            );
          }}
        />
        {watchedStatus === EventRegistrationAdditionalStatus.WEATHER_EVENT && (
          <Banner
            description='PAT events with a "Weather Event" status must be finalized by the PGA of America Membership Team.  Please contact Membership at 800-474-2776 for assistance.'
            variant="info"
            testId="weather-status-info-banner"
            className="my-8 [&_h3]:!font-normal"
          />
        )}
        <Controller
          control={control}
          name="note"
          rules={{ required: "Note is required" }}
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Note"
              type="textarea"
              rows={3}
              value={value}
              onChange={onChange}
              required
              error={!!errors.note}
              errorText={errors.note?.message as string}
            />
          )}
        />
      </div>
    </Modal>
  );
}
